import { useMemo, useState } from "react";

import { Button } from "@/web/components/primitives/button";
import {
  Drawer,
  DrawerCloseIcon,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/web/components/primitives/drawer";
import { formatCompactWholeNumberWithUnit, formatNumberToShort } from "@/web/lib/formatting";
import type { UnderwritableEntitiesV4 } from "@tycho/web-bindings/types";
import { useSuspendedShareBusinessCardContexts } from "@/web/routes/_protected/smb/-dashboard/dashboardData";
import { useTourStep } from "@/web/routes/_protected/smb/-dashboard/-acknowledgments/useTourStep";
import {
  activeBusinessCard,
  presentationFromContext,
  ShareBusinessCardDrawerBody,
  shareableCardUrl,
  shareBusinessCardLink,
} from "@/web/routes/_protected/smb/-dashboard/-actions/share/ShareBusinessCardAction";
import { FundraiseStackedChart } from "@/web/routes/_protected/smb/-dashboard/-footer/-FundraiseStackedChart";
import {
  GenericFooterDrawer,
  GenericFooterDrawerTrigger,
  GenericFooterTileLoader,
} from "@/web/routes/_protected/smb/-dashboard/-footer/-GenericFooterDrawer";
import { resolvePriceEstimateNumber } from "@/web/routes/_protected/smb/-dashboard/comps";
import { useSuspendedSmbSuperView } from "@/web/routes/_protected/smb/-dashboard/superView";

type TargetedProperty = ReturnType<typeof useSuspendedSmbSuperView>["targetedProperties"][number];
type LandlordNegotiationForm = TargetedProperty["landlordNegotiationForm"];

/**
 * Seller's stake in the deal, derived from the landlord's submitted negotiation
 * form (the super view only surfaces submitted forms):
 *   - partial sale: the landlord keeps equity, so the seller-retained portion is
 *     the implied purchase price minus their cash-out. The implied price mirrors
 *     the landlord route's derivation (`landlord/$underwritableEntity.tsx`).
 *   - full sale: the landlord cashes out entirely → seller financing is $0.
 * Returns `undefined` when there's no usable form yet (→ "TBD" in the chart).
 */
function sellerFinancingFromForm(form: LandlordNegotiationForm): number | undefined {
  if (!form) return undefined;
  if (form.saleType?.tag === "Full") return 0;
  if (form.saleType?.tag === "Partial") {
    const { usdCashOut, usdMonthlyDividend, percentAnnualRentalYield } = form;
    if (
      usdCashOut === undefined ||
      usdMonthlyDividend === undefined ||
      percentAnnualRentalYield === undefined ||
      percentAnnualRentalYield <= 0
    ) {
      return undefined;
    }
    // Seller-retained equity = implied purchase price minus the landlord's
    // cash-out. The cash-out cancels (implied price already adds it back), so
    // this is just the capitalized present value of the dividend stream.
    const sellerRetainedEquity = (12 * usdMonthlyDividend) / (percentAnnualRentalYield / 100);
    return Math.max(0, sellerRetainedEquity);
  }
  return undefined;
}

export function FundraiseTileButton({
  underwritableEntityId,
}: {
  underwritableEntityId: UnderwritableEntitiesV4["id"];
}) {
  return (
    <GenericFooterTileLoader>
      <SuspendedFundraiseTileButton underwritableEntityId={underwritableEntityId} />
    </GenericFooterTileLoader>
  );
}

function SuspendedFundraiseTileButton({
  underwritableEntityId,
}: {
  underwritableEntityId: UnderwritableEntitiesV4["id"];
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [shareDrawerOpen, setShareDrawerOpen] = useState(false);
  const tourRef = useTourStep("fundraise");
  const smbInfo = useSuspendedSmbSuperView(underwritableEntityId);
  const firstBusinessCard = smbInfo.businessCards[0];
  const shareContexts = useSuspendedShareBusinessCardContexts();
  const shareContext = useMemo(
    () =>
      shareContexts.find((row) => row.underwritableEntity.compareTo(underwritableEntityId) === 0),
    [shareContexts, underwritableEntityId],
  );
  const sharePresentation = useMemo(() => presentationFromContext(shareContext), [shareContext]);
  // Live (non-soft-deleted) card the manage drawer edits. Every SMB has one
  // from onboarding; only legacy users without a card fall through to the
  // direct-share path in `handleShare`.
  const shareExistingCard = useMemo(
    () => activeBusinessCard(smbInfo.businessCards),
    [smbInfo.businessCards],
  );
  // Read pledges off the LIVE card, not `businessCards[0]` — index 0 can be a
  // soft-deleted card (`smb_super_view` does not filter them), which would show
  // this tile a deleted card's totals while the share drawer below operates on
  // the live one. Same card `activeBusinessCard`/`shareExistingCard` resolves,
  // but kept as the wrapper because that's what carries `allPledges`.
  //
  // It is also the card the server attributes inherited nomination pledges to
  // (the entity's first live card), so a claimed business's community total
  // lands here and is never double-counted across an entity's other cards.
  const liveBusinessCard = useMemo(
    () => smbInfo.businessCards.find((bc) => bc.businessCard.deletedAt === undefined),
    [smbInfo.businessCards],
  );
  const expressionsOfInterest = liveBusinessCard?.allPledges ?? [];

  const totalInterest = expressionsOfInterest.reduce(
    (acc, pledge) => acc + pledge.dollars,
    BigInt(0),
  );
  const communityInterest = Number(totalInterest);

  // ── Derive the four stacked-bar figures from the first targeted property ──
  // Every figure has a resilient fallback: a freshly-onboarded user has none of
  // these set, and the chart still renders sensible TBD/empty states.
  const firstTargetedProperty = smbInfo.targetedProperties[0];
  const contribution = firstTargetedProperty?.userIntendedContribution?.value;

  // Estimated purchase price for the drawer's stacked chart (the tile headline
  // is `totalCommitted` and is unaffected): the user's "max I'd pay" wins if
  // set, then our explicit valuation, then the comps-based average. Each may be
  // undefined → TBD.
  //
  // `resolvePriceEstimateNumber` supplies the priceEstimate-over-comps
  // precedence used everywhere else; `userMaxPrice` stays ahead of both because
  // what the owner will actually pay beats any estimate when funding a gap.
  // NOTE: superView projects `userMaxPrice` as a bare number (it reads
  // `.value`), unlike the card-engine `Property` type's `{value, note}`.
  const estimatedPrice =
    firstTargetedProperty?.userMaxPrice ??
    (firstTargetedProperty
      ? // `priceEstimate` is held out of smb_super_view for now (see superView.ts),
        // so this is the comps-derived estimate.
        resolvePriceEstimateNumber(
          firstTargetedProperty.targetableProperty,
          firstTargetedProperty.topThreeComps,
        )
      : undefined);

  const sellerFinancing = sellerFinancingFromForm(firstTargetedProperty?.landlordNegotiationForm);

  // Hero number: the total capital committed across all three parties.
  const totalCommitted = (contribution ?? 0) + (sellerFinancing ?? 0) + communityInterest;
  const totalCommittedFormattedShort = formatCompactWholeNumberWithUnit(
    formatNumberToShort(totalCommitted),
    "Dollars",
  );
  const totalCommittedFormatted = formatCompactWholeNumberWithUnit(
    Intl.NumberFormat("en-US").format(Math.round(totalCommitted)),
    "Dollars",
  );

  function handleShare() {
    // With a live card, open the manage drawer (share link + visibility/password
    // controls). Without one (legacy), share the first card's link directly.
    if (shareExistingCard) {
      setShareDrawerOpen(true);
    } else if (firstBusinessCard) {
      void shareBusinessCardLink(shareableCardUrl(firstBusinessCard.businessCard.id));
    }
  }

  return (
    <>
      <GenericFooterDrawerTrigger
        ref={tourRef}
        title={totalCommittedFormattedShort}
        subtitle="Fund"
        color="yellow"
        onClick={() => setIsOpen(true)}
      />
      <GenericFooterDrawer title="Fund" isOpen={isOpen} setIsOpen={setIsOpen}>
        <div className="flex flex-col gap-4">
          <section className="flex w-full flex-col items-center justify-center gap-4 bg-withus-yellow p-10 text-center text-withus-blue">
            <span className="withus-strong text-4xl">{totalCommittedFormatted}</span>
            <span>
              Between <strong>your contribution</strong>, <strong>seller financing</strong>, and{" "}
              <strong>investor interest</strong>, this is the amount of capital you have committed
              towards your purchase.
            </span>
            <FundraiseStackedChart
              estimatedPrice={estimatedPrice}
              contribution={contribution}
              sellerFinancing={sellerFinancing}
              communityInterest={communityInterest}
            />
          </section>
          <section className="flex flex-col gap-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold">People interested</span>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  className="bg-withus-yellow px-2 py-1"
                  onClick={handleShare}
                >
                  Share
                </Button>
              </div>
            </div>
            <div className="max-h-[500px] divide-y divide-border overflow-y-scroll border bg-withus-pure-white">
              {expressionsOfInterest.length === 0 && (
                <div className="flex items-center justify-center p-4 text-muted-foreground">
                  Potential investors will appear here once they start expressing interest
                </div>
              )}
              {expressionsOfInterest.map((eoi) => (
                <div key={eoi.id} className="flex items-center justify-between gap-2 p-2">
                  <span className="flex items-center gap-2">
                    {/* TODO: avatar image; real names; etc */}
                    <span className="flex size-8 items-center justify-center rounded-full border">
                      {eoi.displayName.slice(0, 1).toUpperCase()}
                    </span>
                    <span className="max-w-[100px] overflow-hidden font-semibold text-ellipsis">
                      {eoi.displayName}
                    </span>
                  </span>
                  <div className="flex items-center gap-4 text-muted-foreground">
                    <span>
                      {formatCompactWholeNumberWithUnit(
                        Intl.NumberFormat("en-US").format(Number(eoi.dollars)),
                        "Dollars",
                      )}
                    </span>
                    <span>
                      {eoi.pledgedAt
                        .toDate()
                        .toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </section>
        </div>
      </GenericFooterDrawer>

      <Drawer open={shareDrawerOpen} onOpenChange={setShareDrawerOpen}>
        <DrawerContent>
          <DrawerHeader>
            <DrawerTitle>Share with potential investors</DrawerTitle>
            <DrawerCloseIcon />
          </DrawerHeader>
          {shareExistingCard ? (
            <ShareBusinessCardDrawerBody
              presentation={sharePresentation}
              existingCard={shareExistingCard}
              onClose={() => setShareDrawerOpen(false)}
              onComplete={() => setShareDrawerOpen(false)}
            />
          ) : null}
        </DrawerContent>
      </Drawer>
    </>
  );
}
